import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { NavigationScreenSiteInvite } from '@gitbook/app-navigation';
import { LoadingPanel } from '@gitbook/spine-react';

import { NavigationRedirect } from '../../navigation';
import { apiQuery, CurrentOrganizationProvider, SiteContextProvider } from '../../state';
import { SiteMembersAccessDialog } from '../Site/components';

/**
 * Screeen rendering just a dialog to invite/manage members of a site.
 */
export function SiteInviteScreen(props: {
    screen: NavigationScreenSiteInvite;
    isOverlay: boolean;
}): React.ReactElement {
    const { screen, isOverlay } = props;

    // This dialog should always be rendered as an overlay on top of the members screen.
    if (!isOverlay) {
        return (
            <NavigationRedirect
                to={{
                    screen: {
                        type: 'site',
                        organization: screen.organization,
                        site: screen.site,
                        tab: { type: 'members' },
                    },
                    overlay: {
                        type: 'site_invite',
                        organization: screen.organization,
                        site: screen.site,
                    },
                }}
                replace
            />
        );
    }

    return (
        <React.Suspense fallback={<LoadingPanel />}>
            <SiteInviteDialog organizationId={screen.organization} siteId={screen.site} />
        </React.Suspense>
    );
}

function SiteInviteDialog(props: { organizationId: string; siteId: string }): React.ReactElement {
    const { organizationId, siteId } = props;

    const { data: site } = useSuspenseQuery(apiQuery('getSiteById', { organizationId, siteId }));
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );

    return (
        <CurrentOrganizationProvider organization={organization}>
            <SiteContextProvider site={site} organizationId={organizationId}>
                <SiteMembersAccessDialog />
            </SiteContextProvider>
        </CurrentOrganizationProvider>
    );
}
